import { motion, useInView } from 'framer-motion';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import Header from '@/common/header';
import { FaqAccordion } from '@/components/trial/shared/faq-accordion';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { outfit } from '@/public/assets/fonts/Outfit';

import { CreateAccountModal } from '../../create-account-modal';
import { Footer } from '../../footer';
import { NoPingAppVideo } from '../../shared/noping-app-video';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { FooterGlow, GridGlow, SectionFade } from './components/backgrounds';
import { ACCENT_BLUE, ACCENT_CYAN, DARK_BG, EASE_OUT, FAQ_ITEMS_KEYS, GAME_CAROUSEL_ITEMS, HOW_IT_WORKS_STEPS, INFRA_STATS, MUTED_GRAY, NOPING_GREEN, PRO_ENDORSEMENTS, TECH_FEATURES, TEXT_LIGHT } from './components/constants';
import { AnimatedCounter, BeforeAfterSection, LivePingBadge, MulticonnectionBeforeAfter, MulticonnectionViz, PingSliderCompare } from './components/interactive';
import { Reveal, Section, SectionTitle, StepConnector } from './components/ui-helpers';

interface PingDropProps extends DefaultTrialProps {}

export function PingDrop({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: PingDropProps) {
  const { t } = useTranslation(['components/trial/lp/v1b']);
  const [modalOpen, setModalOpen] = useState(false);
  const [pingPercent, setPingPercent] = useState(0);
  const pingRef = useRef<HTMLDivElement>(null);
  const pingInView = useInView(pingRef, { once: true, margin: '-40px' });

  useEffect(() => {
    if (!pingInView) return undefined;
    let current = 0;
    const step = () => {
      current += 1;
      setPingPercent(Math.min(current, 80));
      if (current < 80) requestAnimationFrame(step);
    };
    const timeout = setTimeout(step, 400);
    return () => clearTimeout(timeout);
  }, [pingInView]);

  const faqItems = FAQ_ITEMS_KEYS.map((i) => ({
    q: t(`faq.items.${i}.q`),
    a: t(`faq.items.${i}.a`),
  }));

  return (
    <div className={`${bai_jamjuree.className} ${outfit.variable} relative min-h-screen selection:bg-[#93FF18]/30`} style={{ background: DARK_BG, color: TEXT_LIGHT }}>
      <Header variant='primary' />

      {/* HERO */}
      <section className='relative flex min-h-screen flex-col justify-center overflow-hidden pb-12 pt-20 md:pb-16 md:pt-24'>
        <GridGlow />

        <div ref={pingRef} className='relative z-10 mx-auto max-w-5xl px-4 text-center sm:px-6'>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE_OUT }}>
            <h1 className='mb-6 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl' style={{ fontFamily: 'var(--font-bai)' }}>
              {t('hero.title_prefix')} <span style={{ color: NOPING_GREEN }}>{pingPercent}%</span> {t('hero.title_suffix')}
              <br />
              {t('hero.title_line2')}
            </h1>
            <p className='mx-auto mb-2 max-w-xl text-sm sm:text-base' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
              {t('hero.subtitle')}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5, ease: EASE_OUT }} className='mt-8'>
            <PingSliderCompare />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.4, ease: EASE_OUT }} className='mt-6 flex flex-col items-center gap-4'>
            <div className='inline-flex items-center gap-3 rounded-full px-4 py-2 font-mono text-xs' style={{ color: MUTED_GRAY, border: `1px solid ${ACCENT_CYAN}20`, backgroundColor: `${ACCENT_CYAN}05` }}>
              <span className='size-1.5 animate-pulse rounded-full' style={{ backgroundColor: NOPING_GREEN }} />
              <span style={{ color: `${ACCENT_CYAN}B0` }}>{t('hero.live_reduction')}</span>
              <LivePingBadge />
            </div>

            <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='cursor-pointer rounded-full border-none px-8 py-4 text-base font-bold' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 15px ${NOPING_GREEN}30` }} whileHover={{ scale: 1.05, boxShadow: `0 0 25px ${NOPING_GREEN}50` }} whileTap={{ scale: 0.95 }}>
              {t('hero.cta')}
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <Section bg={<SectionFade />}>
        <SectionTitle label={t('before_after.label')} title={t('before_after.title')} />
        <BeforeAfterSection />
      </Section>

      {/* INFRASTRUCTURE STATS */}
      <Section>
        <SectionTitle label={t('infra_stats.label')} title={t('infra_stats.title')} />
        <div className='grid grid-cols-2 gap-6 md:grid-cols-4 lg:gap-8'>
          {INFRA_STATS.map((stat, i) => (
            <Reveal key={stat.label} delay={i * 0.1}>
              <div className='group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center transition-colors hover:border-[#93FF18]/30'>
                <div className='absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100' style={{ background: `radial-gradient(circle at center, ${NOPING_GREEN}05, transparent)` }} />
                <p className='relative z-10 text-3xl font-black sm:text-4xl md:text-5xl' style={{ color: NOPING_GREEN }}>
                  <AnimatedCounter target={stat.target} suffix={stat.suffix} duration={2.5} />
                </p>
                <p className='relative z-10 mt-2 text-sm font-semibold uppercase tracking-wider text-white/80' style={{ fontFamily: 'var(--font-outfit)' }}>
                  {t(stat.label)}
                </p>
                <p className='relative z-10 mt-1 text-xs' style={{ color: MUTED_GRAY }}>
                  {t(stat.sublabel)}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section bg={<SectionFade />}>
        <SectionTitle label={t('how_it_works.label')} title={t('how_it_works.title')} />
        <div className='flex flex-col items-center justify-center gap-6 md:flex-row md:gap-0'>
          {HOW_IT_WORKS_STEPS.map((step, i) => (
            <div key={step.step} className='contents'>
              <Reveal delay={i * 0.15} className='flex max-w-[240px] flex-col items-center text-center'>
                <div className='mb-4 flex size-16 items-center justify-center rounded-2xl' style={{ backgroundColor: `${NOPING_GREEN}10`, border: `1px solid ${NOPING_GREEN}20`, color: NOPING_GREEN }}>
                  <svg className='size-8' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
                    <path strokeLinecap='round' strokeLinejoin='round' d={step.iconPath} />
                  </svg>
                </div>
                <span className='mb-1 font-mono text-xs' style={{ color: `${ACCENT_CYAN}60` }}>
                  {t('how_it_works.step_label')} {step.step}
                </span>
                <h3 className='mb-2 text-xl font-bold' style={{ fontFamily: 'var(--font-bai)' }}>
                  {t(step.titleKey)}
                </h3>
                <p className='text-sm leading-relaxed' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t(step.descKey)}
                </p>
              </Reveal>
              {i < HOW_IT_WORKS_STEPS.length - 1 && <StepConnector />}
            </div>
          ))}
        </div>
      </Section>

      {/* MULTICONNECTION VISUALIZATION */}
      <Section className='overflow-hidden' bg={<SectionFade />}>
        <SectionTitle label={t('multiconnection.label')} title={t('multiconnection.title')} description={t('multiconnection.description')} />
        <Reveal>
          <div className='relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8'>
            <div className='absolute inset-0 opacity-[0.03]'>
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={`mg-h-${i}`} className='absolute w-full border-t' style={{ top: `${(i + 1) * 5}%`, borderColor: ACCENT_BLUE }} />
              ))}
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={`mg-v-${i}`} className='absolute h-full border-s' style={{ left: `${(i + 1) * 5}%`, borderColor: ACCENT_BLUE }} />
              ))}
            </div>
            <div className='relative z-10'>
              <MulticonnectionViz />
              <MulticonnectionBeforeAfter />
            </div>
          </div>
        </Reveal>
      </Section>

      {/* TECHNOLOGY */}
      <Section>
        <SectionTitle label={t('tech.label')} title={t('tech.title')} />
        <div className='grid gap-6 md:grid-cols-3 lg:gap-8'>
          {TECH_FEATURES.map((tech, i) => (
            <Reveal key={tech.titleKey} delay={i * 0.15}>
              <div className='group h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-all duration-300 hover:border-[#93FF18]/30 md:p-8'>
                <div className='mb-5 flex size-12 items-center justify-center rounded-xl transition-colors group-hover:bg-opacity-20' style={{ backgroundColor: `${ACCENT_CYAN}10`, border: `1px solid ${ACCENT_CYAN}20`, color: ACCENT_CYAN }}>
                  <svg className='size-7' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
                    <path strokeLinecap='round' strokeLinejoin='round' d={tech.iconPath} />
                  </svg>
                </div>
                <h3 className='mb-3 text-xl font-bold' style={{ fontFamily: 'var(--font-bai)' }}>
                  {t(tech.titleKey)}
                </h3>
                <p className='mb-4 text-sm leading-relaxed' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t(tech.descKey)}
                </p>
                <div className='inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-xs' style={{ color: NOPING_GREEN, backgroundColor: `${NOPING_GREEN}10` }}>
                  <span className='size-1.5 rounded-full' style={{ backgroundColor: NOPING_GREEN }} />
                  {t(tech.metricKey)}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* APP DASHBOARD */}
      <Section className='overflow-hidden' bg={<SectionFade />}>
        <SectionTitle label={t('dashboard.label')} title={t('dashboard.title')} description={t('dashboard.description')} />
        <Reveal>
          <div className='mx-auto max-w-4xl'>
            <div className='overflow-hidden rounded-2xl border border-white/10 shadow-2xl'>
              <NoPingAppVideo />
            </div>
          </div>
        </Reveal>
      </Section>

      {/* PRO VALIDATION */}
      <Section bg={<SectionFade />}>
        <SectionTitle label={t('pros.label')} title={t('pros.title')} />
        <div className='grid gap-6 md:grid-cols-3 lg:gap-8'>
          {PRO_ENDORSEMENTS.map((pro, i) => (
            <Reveal key={pro.name} delay={i * 0.15}>
              <div className='relative h-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8'>
                <div className='absolute end-4 top-4 flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px]' style={{ color: `${NOPING_GREEN}CC`, backgroundColor: `${NOPING_GREEN}10` }}>
                  <svg className='size-3' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd' d='M16.403 12.652a3 3 0 010-5.304 3 3 0 00-3.75-3.751 3 3 0 00-5.305 0 3 3 0 00-3.751 3.75 3 3 0 000 5.305 3 3 0 003.75 3.751 3 3 0 005.305 0 3 3 0 003.751-3.75zm-6.028-1.397a.75.75 0 01.175 1.045l-.001.001-.003.003a1 1 0 01-.04.049 2 2 0 01-.108.119 1.5 1.5 0 01-.396.303c-.205.108-.479.2-.835.2-.356 0-.63-.092-.835-.2a1.5 1.5 0 01-.396-.303 2 2 0 01-.148-.168l-.003-.003v-.002a.75.75 0 011.22-.87l.004.005a.25.25 0 00.047.04.3.3 0 00.111.037c.054 0 .111-.012.167-.045a.25.25 0 00.047-.04l.004-.005a.75.75 0 011.045-.175zm-2.196-2.632a.75.75 0 111.042 1.08l-.001.001-.003.003-.007.007a1 1 0 01-.086.079.75.75 0 01-.945-1.17zm4.922 0a.75.75 0 111.042 1.08l-.001.001-.003.003-.007.007a1 1 0 01-.086.079.75.75 0 01-.945-1.17z' clipRule='evenodd' />
                  </svg>
                  {t('pros.verified')}
                </div>
                <div className='mb-5'>
                  <div className='relative size-14 overflow-hidden rounded-full border border-white/10'>
                    <Image src={pro.image} alt={pro.name} width={56} height={56} className='size-full rounded-full object-cover' />
                  </div>
                </div>
                <h3 className='mb-1 text-xl font-bold' style={{ fontFamily: 'var(--font-bai)' }}>
                  {pro.name}
                </h3>
                <p className='mb-4 font-mono text-xs uppercase tracking-wider' style={{ color: ACCENT_CYAN }}>
                  {t(`pros.items.${i}.title`)} / {t(`pros.items.${i}.game`)}
                </p>
                <blockquote className='border-s-2 ps-4 text-sm italic leading-relaxed' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)', borderColor: `${NOPING_GREEN}30` }}>
                  &ldquo;{t(pro.quoteKey)}&rdquo;
                </blockquote>
                <div className='mt-6 flex items-center gap-4 font-mono text-xs' style={{ color: MUTED_GRAY }}>
                  <div className='flex items-center gap-1.5'>
                    <span className='size-2 rounded-full' style={{ backgroundColor: NOPING_GREEN }} />
                    {t('pros.avg_ping')}: {pro.ping}
                  </div>
                  <div className='flex items-center gap-1.5'>
                    <span className='size-2 rounded-full' style={{ backgroundColor: ACCENT_CYAN }} />
                    {t('pros.uptime')}: 99.9%
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* GAMES SUPPORTED */}
      <Section>
        <SectionTitle label={t('games.label')} title={t('games.title')} description={t('games.description')} />
        <div className='relative overflow-hidden' style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}>
          <div className='game-carousel-track flex items-center gap-5 py-6'>
            {[...GAME_CAROUSEL_ITEMS, ...GAME_CAROUSEL_ITEMS].map((game, i) => (
              <div key={`${game.name}-${i}`} className='group w-[190px] shrink-0'>
                <div className='flex flex-col items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 transition-all duration-200 group-hover:scale-105 group-hover:border-[#93FF18]/40'>
                  <div className='relative h-[220px] w-[160px] overflow-hidden rounded-xl'>
                    <Image src={game.src} alt={game.name} fill className='object-cover' sizes='160px' />
                  </div>
                  <span className='w-full truncate text-center text-sm font-semibold' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {game.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <style>{`
            .game-carousel-track {
              animation: gameScroll 40s linear infinite;
              width: max-content;
            }
            .game-carousel-track:hover {
              animation-play-state: paused;
            }
            @keyframes gameScroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            /* transform is physical — CSS never mirrors it for writing direction. The track is
               width: max-content inside a narrower clip box, so under the dir="rtl" that /sa
               inherits from <html> the over-constrained box anchors to the RIGHT edge and overflows
               left, and the negative shift drags the strip out of the clip box instead of through it.
               Mirrored keyframes shift positive; only animation-name is overridden. */
            @keyframes gameScrollRtl {
              0% { transform: translateX(0); }
              100% { transform: translateX(50%); }
            }
            [dir='rtl'] .game-carousel-track {
              animation-name: gameScrollRtl;
            }
          `}</style>
        </div>
      </Section>

      {/* FAQ */}
      <Section bg={<SectionFade />}>
        <SectionTitle label={t('faq.label')} title={t('faq.title')} />
        <div className='mx-auto max-w-3xl'>
          <FaqAccordion items={faqItems} accentColor={NOPING_GREEN} />
        </div>
      </Section>

      {/* FOOTER CTA */}
      <section className='relative overflow-hidden py-24 md:py-32'>
        <FooterGlow />
        <div className='relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6'>
          <Reveal>
            <h2 className='mb-4 text-3xl font-bold sm:text-4xl md:text-5xl lg:text-6xl' style={{ fontFamily: 'var(--font-bai)' }}>
              {t('cta.title_prefix')} <span style={{ color: NOPING_GREEN }}>{t('cta.title_accent')}</span>
            </h2>
            <p className='mx-auto mb-10 max-w-xl text-base sm:text-lg' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
              {t('cta.description')}
            </p>
            <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='cursor-pointer rounded-full border-none px-10 py-4 text-lg font-bold' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 20px ${NOPING_GREEN}25` }} whileHover={{ scale: 1.05, boxShadow: `0 0 30px ${NOPING_GREEN}40` }} whileTap={{ scale: 0.95 }}>
              {t('cta.button')}
            </motion.button>
          </Reveal>
        </div>
      </section>

      <Footer text={t('footer', { year: new Date().getFullYear() })} />

      <CreateAccountModal open={modalOpen} onClose={() => setModalOpen(false)} daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v1b-modal-form' />
    </div>
  );
}
